import type { StockoutFigures } from "~/components/demand-2/demand/StockoutsBreakdown"
import { signedPct, signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { AnswerPicker } from "~/components/demand-2/shared/FramingAnswerPicker"
import { useScopedFramingAnswer } from "~/components/demand-2/shared/useScopedFramingAnswer"
import {
  scopeNode,
  type AnswerScope,
  type FramingDescribeContext,
  type FramingNode,
  WHOLE_COLLECTION_NODE,
} from "~/utils/demandFraming"

/** The `stockout` question, asked under the chart that shows the measurement. */
const StockoutsAnswer = ({
  node: nodeProp = WHOLE_COLLECTION_NODE,
  scope: scopeProp,
  figures,
}: {
  /** Defaults to the whole collection; a node falls back to the collection's answer. */
  node?: FramingNode
  /** Overrides `node`, e.g. one variant from the Total page. */
  scope?: AnswerScope
  /** The scope's comparables: what they sold, and what they lost while out of stock. */
  figures: StockoutFigures
}) => {
  const read = useScopedFramingAnswer("stockout", nodeProp, scopeProp)
  if (read == null) return null
  const { collectionId, kind, scope, answer, inherited, isDefault } = read
  // NOOS measures on exactly what the baseline reads (own sales or matches), not on comparables.
  const ownSales = kind === "noos"
  const node = scopeNode(scope)
  const context: FramingDescribeContext = {
    stockout: {
      nodeName: node.groupKey === "" ? undefined : node.groupValue,
      ownSales,
      comparableSales: figures.comparableSales,
      lostUnits: figures.lostUnits,
    },
  }
  // What each answer comes to on this scope.
  const impactOf = (optionId: string): React.ReactNode => {
    if (figures.lostUnits <= 0) return null
    if (optionId === "ignore") {
      return <span className="paragraph-sm text-fg-subtle">No change to the plan</span>
    }
    return (
      <span className="paragraph-sm text-fg-subtle">
        <span className="text-fg-success-icon">
          {signedUnits(figures.lostUnits)} units
          {figures.comparableSales > 0
            ? ` · ${signedPct(figures.lostUnits / figures.comparableSales)}`
            : ""}
        </span>{" "}
        {ownSales ? "on what the baseline sold" : "on what the comparables sold"}
      </span>
    )
  }
  return (
    <AnswerPicker
      collectionId={collectionId}
      question="stockout"
      answer={answer}
      scope={scope}
      inherited={inherited}
      isDefault={isDefault}
      describeContext={context}
      optionFooter={impactOf}
    />
  )
}

export default StockoutsAnswer
